'use memo'

import {
  PropertyAddress,
  DisplayElement,
  AgxFileUpload,
  AgxTenantedPropertyDetails,
  TenantedProperty,
  Tenancy,
  AgxPropertyAddressForm,
  DocumentTypes,
  AgxTenancies,
  conditionsAreMet,
  AgxChattels,
  ChattelsIncluded,
  FileUpload,
  Dropdown,
  ConditionalTextInput,
  TypedDocumentUpload,
  ContractType,
  Currency,
  AddressSearchElement,
  AlertDetails,
  SolicitorDetails,
  AgxSolicitorDetails,
  SolicitorSearchElement,
  TenantedPropertyElement,
  ChattelsIncludedType,
  SellersDisclosureUploadModel,
  AgxBodyCorporate,
  BodyCorporateDetails,
  BodyCorporateElement,
  CommunityTitlesSchemeDetails,
  CommunityTitlesSchemeElement,
  PropertyAttributesDisplayElement,
  AgxPropertyAttributes,
  RadioElement,
  EditablePdfUploadConfig,
  EditablePdfDocumentModel,
  SurveyPlanUploadModel,
  ElementType,
} from '@urbanx/agx-ui-components'
import {
  AgxDropdown,
  AgxVendorBuyers,
  AgxRadioGroup,
  AgxRow,
  AgxTypography,
  AgxSpecialConditionsPicker,
  AgxAlerts,
  AgxConditionalTextInput,
  AgxCheckbox,
  AgxTextInput,
  AgxYesNoQuestion,
} from '@urbanx/agx.ui-components2'
import { isNil } from 'lodash-es'
import { FormValue, FormValueType } from 'types/formConfig'
import {
  deleteFile,
  getFileLink,
  uploadFile,
  editFile,
  GetPdfLicenseKey,
} from 'services'
import { YesNo } from 'types/displayElements'
import EditablePdfUpload from 'pdf/EditablePdfUpload/EditablePdfUpload'
import OwnerBuilderDeclaration from 'components/ownerBuilderDeclaration/OwnerBuilderDeclaration'
import { OwnerBuilderDeclarationModel } from 'components/ownerBuilderDeclaration/types'
import SellersDisclosureUpload from 'components/sellers-disclosure-upload/SellersDisclosureUpload'
import CommunityTitlesScheme from 'components/community-titles-scheme/CommunityTitlesScheme'
import SurveyPlanUpload from 'components/survey-plan-upload/SurveyPlanUpload'
import LegalDescription from 'pages/campaigns/components/legalDescription/LegalDescription'
import {
  CampaignDetailModel,
  IdentityVerificationExportsModel,
  LegalDescriptionModel,
  SpecialConditionData,
  SpecialConditionPicker,
  VendorBuyerDetails,
} from '@urbanx/agx.campaigns.client'
import IdentityVerificationExportsElement from 'pages/campaigns/components/manageCampaign/Components/IdentityVerificationExportsElement'

interface Props {
  elements: DisplayElement[]
  campaign: CampaignDetailModel
  getAuthToken: () => Promise<string | void>
  onChange: (change: {
    id: string
    value: FormValueType[keyof FormValueType]
  }) => void
  formValues: FormValue
  validate: boolean
}

export default function elementRenderer(props: Props) {
  const {
    elements,
    onChange,
    campaign,
    getAuthToken,
    formValues,
    validate,
  } = props

  if (!elements) return

  const onAddressSelected = (
    id: string,
    address: PropertyAddress | undefined
  ) => {
    onChange({
      id,
      value: address as PropertyAddress,
    })
  }

  const renderElement = (element: DisplayElement) => {
    const currentValue = Object.prototype.hasOwnProperty.call(
      formValues,
      element.id
    )
      ? formValues[element.id]
      : element.value

    const required =
      conditionsAreMet(element.requiredConditions, formValues) ||
      (element.required ?? false)
    const disabled =
      conditionsAreMet(element.disableConditions, formValues) ||
      (element.disabled ?? false)

    switch (element.elementType) {
      case ElementType.Alerts: {
        const alertDetails = element as AlertDetails
        return (
          <div key={`container-${element.id}`}>
            <AgxAlerts
              id={alertDetails.id}
              dismissedAlertIds={currentValue ?? []}
              alerts={alertDetails?.alerts ?? []}
              onValueChanged={onChange}
            ></AgxAlerts>
          </div>
        )
      }
      case ElementType.Dropdown: {
        const dropdownOptions = (element as Dropdown).options
        const dropdownValue =
          dropdownOptions.find((option) => option.value === currentValue) ??
          dropdownOptions[0]

        return (
          <div key={`container-${element.id}`}>
            <AgxDropdown
              id={element.id}
              key={`${element.id}`}
              options={dropdownOptions}
              selected={dropdownValue.value}
              label={element.label ?? ''}
              hideOptionalText={required}
              type="single"
              required={required}
              onChange={(value) => onChange({ id: element.id, value })}
            />
          </div>
        )
      }

      case ElementType.YesNoQuestion: {
        return (
          <div key={`container-${element.id}`}>
            <AgxYesNoQuestion
              id={element.id}
              key={`${element.id}`}
              onValueChanged={onChange}
              defaultValue={currentValue}
              label={element.label ?? ''}
              subDescription={element.subDescription ?? ''}
              yesLabel={(element as YesNo).yesLabel}
              noLabel={(element as YesNo).noLabel}
              reverseLabelOrder={(element as YesNo).reverseLabelOrder}
              required={required}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.VendorBuyerDetails: {
        const vendorElement = element as VendorBuyerDetails

        return (
          <div key={`container-${element.id}`}>
            <AgxVendorBuyers
              id={element.id}
              key={`${element.id}`}
              onChange={({ value }) =>
                onChange({
                  id: element.id,
                  value: value as FormValueType[keyof FormValueType],
                })
              }
              propertyAddress={campaign.address}
              defaultValue={currentValue}
              editScope={vendorElement.editScope}
              stakeholderType={vendorElement.stakeholderType}
              vendorBuyerTypes={vendorElement.vendorBuyerTypes}
              companyVendorBuyerTypes={vendorElement.companyVendorBuyerTypes}
              vendorBuyerConfig={vendorElement.vendorBuyerConfig}
              powerOfAttorneyConfig={vendorElement.powerOfAttorneyConfig ?? {}}
              companyConfig={vendorElement.companyConfig ?? {}}
              required={required}
              campaignId={campaign.campaignId ?? ''}
              displayLegalName={vendorElement.displayLegalName}
              registeredOwnerNames={vendorElement.registeredOwnerNames ?? []}
              agents={[]}
              leadAgentId={''}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.IdentityVerificationExports: {
        return (
          <div key={`container-${element.id}`}>
            <IdentityVerificationExportsElement
              id={element.id}
              campaignId={campaign.campaignId ?? ''}
              validate={validate}
              value={currentValue as IdentityVerificationExportsModel | undefined}
            />
          </div>
        )
      }

      case ElementType.SolicitorSearch: {
        const solicitorElement = element as SolicitorSearchElement

        return (
          <div key={`container-${element.id}`}>
            <AgxSolicitorDetails
              id={element.id}
              key={`${element.id}`}
              onValueUpdated={onChange}
              defaultValue={currentValue as SolicitorDetails}
              label={solicitorElement.label ?? ''}
              solicitorLabel={solicitorElement.solicitorLabel}
              required={solicitorElement.required}
              validate={validate}
              useLongStreetNameInFormattedAddress
            />
          </div>
        )
      }
      case ElementType.TenancyDetails: {
        return (
          <div key={`container-${element.id}`}>
            <AgxTenancies
              id={element.id}
              key={`${element.id}`}
              onValueChanged={onChange}
              defaultTenancies={currentValue as Tenancy[]}
              validate={validate}
            />
          </div>
        )
      }
      case ElementType.AddressSearch: {
        return (
          <div key={`container-${element.id}`}>
            <AgxPropertyAddressForm
              key={element.id}
              id={element.id}
              label={element.label ?? ''}
              onAddressSelected={(address) =>
                onAddressSelected(element.id, address)
              }
              allowParentUpdates
              defaultValue={currentValue as PropertyAddress}
              propertyAddressField={false}
              showSearchForAddress={false}
              hideUnitLot={
                formValues?.ContractType !== ContractType.CommunityTitles
              }
              required={required}
              validate={validate}
              stateToSearch={
                (element as AddressSearchElement).stateToSearch ?? undefined
              }
            />
          </div>
        )
      }

      case ElementType.TextInput: {
        return (
          <div key={`container-${element.id}`}>
            <AgxTextInput
              id={element.id}
              key={`${element.id}`}
              onInputValueChange={onChange}
              label={element.label ?? ''}
              stretch
              hideOptionalText={required}
              defaultValue={(currentValue as string) ?? ''}
              required={required}
              disabled={disabled}
              placeholder={element.placeholder ?? ''}
              readonly={element.isReadonly}
              hintText={element.subDescription ?? ''}
              placeholderPosition={
                element.rightAlignedPlaceholder ? 'right' : 'left'
              }
              errorText={
                validate &&
                required &&
                ((currentValue as string) === '' || isNil(currentValue))
                  ? `Enter ${element.label}`
                  : ''
              }
              type={'text'}
            />
          </div>
        )
      }

      case ElementType.PropertyAttributes: {
        const { hidePropertyAttributes } =
          element as PropertyAttributesDisplayElement

        return (
          <div key={`container-${element.id}`}>
            <AgxPropertyAttributes
              id={element.id}
              key={`${element.id}`}
              label={element.label}
              onChange={onChange}
              defaultValue={currentValue}
              hidePropertyAttributes={hidePropertyAttributes}
              required={required}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.LegalDescription: {
        return (
          <div key={`container-${element.id}$`}>
            <LegalDescription
              id={element.id}
              key={`${element.id}`}
              label={element.label}
              readonly={element.isReadonly}
              onChange={onChange}
              legalDescription={currentValue as LegalDescriptionModel}
              required={required}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.ConditionalTextInput: {
        const conditionalTextInput = element as ConditionalTextInput

        return (
          <div key={`container-${element.id}`}>
            <AgxConditionalTextInput
              id={element.id}
              key={`${element.id}`}
              onValueChanged={onChange}
              defaultValue={currentValue as string}
              label={element.label ?? ''}
              maxLength={conditionalTextInput.maxLength}
              minLength={conditionalTextInput.minLength ?? 0}
              inputType={conditionalTextInput.inputType}
              noLabel={conditionalTextInput.noLabel ?? 'No'}
              yesLabel={conditionalTextInput.yesLabel ?? 'Yes'}
              reverseLabelOrder={conditionalTextInput.reverseLabelOrder}
              showTextInputOnYesSelection={
                conditionalTextInput.showTextInputOnYesSelection
              }
              noTextInputValue={conditionalTextInput.noTextInputValue}
              placeholder={element.placeholder ?? ''}
              subDescription={element.subDescription ?? ''}
              disabled={disabled}
              readonly={element.isReadonly}
              required={required}
              flexibleHeight={conditionalTextInput.flexibleHeight}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.FileUpload: {
        const fileUploadElement = element as FileUpload
        return (
          <div key={`container-${element.id}`}>
            <AgxFileUpload
              id={element.id}
              key={`${element.id}`}
              fileData={currentValue}
              campaignId={campaign.campaignId}
              documentType={
                fileUploadElement.documentType ?? DocumentTypes.Miscellaneous
              }
              onValueChanged={onChange}
              multiple={fileUploadElement.multiple ?? false}
              getAuthToken={getAuthToken}
              extensions={fileUploadElement.extensions ?? []}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              required={required}
              readonly={element.isReadonly ?? false}
              title={element.label ?? ''}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.EditablePdfUpload: {
        const editablePdfUpload = element as EditablePdfUploadConfig
        return (
          <div key={`container-${element.id}`}>
            <EditablePdfUpload
              id={element.id}
              key={`${element.id}`}
              defaultValue={currentValue as EditablePdfDocumentModel[]}
              campaignId={campaign.campaignId ?? ''}
              documentType={
                editablePdfUpload.documentType ?? DocumentTypes.Miscellaneous
              }
              buttonGroups={editablePdfUpload.buttonGroups}
              getLicenseKey={GetPdfLicenseKey}
              onValueChanged={({ id, value }) => onChange({ id, value })}
              multiple={editablePdfUpload.multiple ?? false}
              getAuthToken={getAuthToken}
              uploadFile={uploadFile}
              editFile={editFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              required={required}
              requiredPdfFields={editablePdfUpload.requiredPdfFields}
              readonly={element.isReadonly ?? false}
              title={element.label ?? ''}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.TypedDocumentUpload:
        return (element as TypedDocumentUpload).documentConfig?.map(
          (config) => (
            <div key={`container-${element.id}`}>
              <AgxFileUpload
                id={element.id}
                key={`${element.id}`}
                fileData={currentValue}
                campaignId={campaign.campaignId}
                documentType={
                  config?.documentType ?? DocumentTypes.Miscellaneous
                }
                onValueChanged={onChange}
                multiple={config?.multipleFiles ?? false}
                getAuthToken={getAuthToken}
                extensions={['pdf']}
                uploadFile={uploadFile}
                deleteFile={deleteFile}
                getFileLink={getFileLink}
                required={config?.required ?? required}
                readonly={config?.readonly ?? false}
                validate={validate}
              />
            </div>
          )
        )

      case ElementType.Checkbox:
        return (
          <div className="checkboxContainer" key={`container-${element.id}`}>
            <AgxCheckbox
              id={element.id}
              key={`${element.id}`}
              onChange={(value) => onChange({ id: element.id, value })}
              label={element.label}
              checked={!!currentValue}
              disabled={disabled}
              subLabel={element.subLabel}
            />
          </div>
        )

      case ElementType.Radio: {
        const { options: radioOptions } = element as RadioElement
        return (
          <div key={`container-${element.id}`}>
            {element.label && (
              <AgxRow padding={{ top: 'spacing-lg', bottom: 'spacing-lg' }}>
                <AgxTypography
                  id={`radio-label-${element.id}`}
                  type="label"
                  size="medium"
                >
                  {element.label}
                </AgxTypography>
              </AgxRow>
            )}
            <AgxRadioGroup
              key={element.id}
              id={element.id}
              onChange={(val) => onChange({ id: element.id, value: val })}
              value={currentValue}
              disabled={disabled}
              layout="stacked"
              options={radioOptions}
              required={required}
              validate={validate}
              errorText={
                validate &&
                required &&
                ((currentValue as string) === '' || isNil(currentValue))
                  ? `Enter ${element.label}`
                  : ''
              }
            />
          </div>
        )
      }

      case ElementType.TenantedProperty: {
        const tenantedPropertyElement = element as TenantedPropertyElement

        return (
          <div key={`container-${element.id}`}>
            <AgxTenantedPropertyDetails
              id={element.id}
              key={`${element.id}`}
              onValueUpdated={onChange}
              defaultValue={currentValue as TenantedProperty}
              isManagingAgentEmailRequired={true}
              isManagingAgentNameRequired={true}
              label={element.label ?? ''}
              validate={validate}
              showHolidayRentalOption={
                tenantedPropertyElement.showHolidayRental
              }
              showPreviousTenancyDetails={
                !!tenantedPropertyElement.showPreviousTenancyDetails
              }
              lastRentIncreaseRequired={
                tenantedPropertyElement.lastRentIncreaseRequired
              }
            />
          </div>
        )
      }

      case ElementType.Currency:
        return (
          <div key={`container-${element.id}`}>
            <AgxTextInput
              id={element.id}
              key={`${element.id}`}
              label={element.label}
              placeholder={element.placeholder ?? ''}
              required={required}
              onInputValueChange={onChange}
              readonly={element.isReadonly}
              disabled={disabled}
              stretch
              defaultValue={currentValue as string}
              hintText={element.subDescription ?? ''}
              type="currency"
              errorText={
                validate &&
                required &&
                ((currentValue as string) === '' || isNil(currentValue))
                  ? `Enter ${element.label}`
                  : ''
              }
              noDecimalPoints={(element as Currency).decimalPoints === 0}
            />
          </div>
        )

      case ElementType.SpecialConditions: {
        const specialConditionElement = element as SpecialConditionPicker

        return (
          <div key={`container-${element.id}`}>
            <AgxSpecialConditionsPicker
              id={element.id}
              key={`${element.id}`}
              onValueChanged={onChange}
              selectedSpecialConditions={
                (formValues.SpecialConditions as SpecialConditionData[]) ?? []
              }
              campaignId={campaign.campaignId ?? ''}
              displayPredefinedConditions={
                specialConditionElement.displayPredefinedConditions ?? true
              }
              customConditionLabel={specialConditionElement.label}
              customConditionSubDescription={specialConditionElement.subDescription}
              validate={validate}
              vendors={campaign.vendors ?? []}
            />
          </div>
        )
      }

      case ElementType.ChattelsIncluded: {
        const chattelsIncludedElement = element as ChattelsIncludedType

        return (
          <div key={`container-${element.id}`}>
            <AgxChattels
              key={`container-${element.id}`}
              label={element.label ?? ''}
              id={element.id}
              defaultValue={element.value as ChattelsIncluded}
              state={campaign?.address?.state ?? ''}
              onValueChange={onChange}
              validate={validate}
              flexibleHeight={chattelsIncludedElement.flexibleHeight}
            />
          </div>
        )
      }

      case ElementType.OwnerBuilderDeclaration: {
        const editablePdfUpload = element as EditablePdfUploadConfig
        return (
          <div key={`container-${element.id}`}>
            <OwnerBuilderDeclaration
              key={`container-${element.id}`}
              id={element.id}
              onChange={onChange}
              getAuthToken={getAuthToken}
              campaignId={campaign.campaignId ?? ''}
              documentType={editablePdfUpload.documentType}
              buttonGroups={editablePdfUpload.buttonGroups}
              customScript={editablePdfUpload.customScript}
              defaultValue={element.value as OwnerBuilderDeclarationModel}
              editFile={editFile}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              getLicenseKey={GetPdfLicenseKey}
              required={element.required}
              requiredPdfFields={editablePdfUpload.requiredPdfFields}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.SellersDisclosureUpload: {
        const sellersDisclosureUpload = element as EditablePdfUploadConfig
        return (
          <div key={`container-${element.id}`}>
            <SellersDisclosureUpload
              id={element.id}
              defaultValue={currentValue as SellersDisclosureUploadModel}
              campaignId={campaign.campaignId ?? ''}
              documentType={sellersDisclosureUpload.documentType}
              buttonGroups={sellersDisclosureUpload.buttonGroups}
              customScript={sellersDisclosureUpload.customScript}
              getAuthToken={getAuthToken}
              onChange={onChange}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              editFile={editFile}
              getFileLink={getFileLink}
              getLicenseKey={GetPdfLicenseKey}
              required={required}
              requiredPdfFields={sellersDisclosureUpload.requiredPdfFields}
              validate={validate}
              multiple={sellersDisclosureUpload.multiple}
            />
          </div>
        )
      }

      case ElementType.BodyCorporate: {
        const bodyCorporateElement = element as BodyCorporateElement
        return (
          <div key={`container-${element.id}`}>
            <AgxBodyCorporate
              id={element.id}
              key={element.id}
              showUploadDocument={bodyCorporateElement.showUploadDocument}
              onValueChanged={onChange}
              defaultValue={currentValue as BodyCorporateDetails}
              label={element.label ?? ''}
              campaignId={campaign.campaignId ?? ''}
              getAuthToken={getAuthToken}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              required={required}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.CommunityTitlesScheme: {
        const communityTitlesSchemeElement =
          element as CommunityTitlesSchemeElement
        return (
          <div key={`container-${element.id}`}>
            <CommunityTitlesScheme
              id={element.id}
              key={element.id}
              onChange={onChange}
              defaultValue={currentValue as CommunityTitlesSchemeDetails}
              label={element.label ?? ''}
              campaignId={campaign.campaignId ?? ''}
              documentType={communityTitlesSchemeElement.documentType}
              extensions={communityTitlesSchemeElement.extensions}
              multiple={communityTitlesSchemeElement.multiple}
              isSortable={communityTitlesSchemeElement.isSortable}
              getAuthToken={getAuthToken}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              required={required}
              validate={validate}
            />
          </div>
        )
      }

      case ElementType.SurveyPlanUpload: {
        const surveyPlanUploadElement = element as FileUpload
        return (
          <div key={`container-${element.id}`}>
            <SurveyPlanUpload
              id={element.id}
              key={element.id}
              onChange={onChange}
              defaultValue={currentValue as SurveyPlanUploadModel}
              label={element.label ?? ''}
              campaignId={campaign.campaignId ?? ''}
              getAuthToken={getAuthToken}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              getFileLink={getFileLink}
              required={required}
              validate={validate}
              multiple={surveyPlanUploadElement.multiple}
              isSortable={surveyPlanUploadElement.isSortable}
              extensions={surveyPlanUploadElement.extensions}
              documentType={surveyPlanUploadElement.documentType}
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  return <>{elements.map(renderElement)}</>
}
